import {
  StepConfigInterface,
  StepIdEnum,
  StepInterface,
  StepsFlowEnum,
  bookForOtherFlow,
  bookForOtherWithStoreFlow,
  defaultFlow,
  serviceFlow,
  storeLocatorFlow,
  superModelFlow,
} from '@/config/steps.config';
import { signIn, useSession } from 'next-auth/react';
import {
  useParams,
  usePathname,
  useRouter,
  useSearchParams,
} from 'next/navigation';
import { useCallback, useEffect, useMemo } from 'react';
import { DatadogRumEnum } from '@/enums/datadog.enum';
import { datadogRum } from '@datadog/browser-rum';
import { RoutesEnum } from '@/enums/routes.enum';
import { mergeURLSearchParams } from '@repo/common/helpers';
import { AuthProviderEnum } from '@repo/common/enums';
import { useWidgetStore } from '@/providers/widget.provider';

export const DEFAULT_COUNTRY = 'FR';

const useConfig = () => {
  const params = useParams<{ locale: string; countryCode: string }>();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const { data: session } = useSession();
  const { shop, countryService } = useWidgetStore((state) => state);

  const countryPrefix = useMemo(() => {
    return `/country/${params?.countryCode?.toUpperCase() ?? DEFAULT_COUNTRY}`;
  }, [params]);

  const builtUrl = useCallback(
    (path?: string) => {
      if (!path) {
        return undefined;
      }

      const url = `${countryPrefix}/steps${path}`;

      if (searchParams?.size > 0) {
        return `${url}?${searchParams?.toString()}`;
      }

      return url;
    },
    [searchParams, countryPrefix],
  );

  const parsedUrl = useCallback(
    (url?: string, _searchParams: URLSearchParams = new URLSearchParams()) => {
      const _url = url?.split('?')?.[0];
      if (!_url) {
        return undefined;
      }

      const newSearchParams = mergeURLSearchParams(searchParams, _searchParams);

      if (newSearchParams?.size > 0) {
        return `${_url}?${newSearchParams?.toString()}`;
      }

      return _url;
    },
    [searchParams],
  );

  const mode: StepsFlowEnum = useMemo(() => {
    const modeParams = searchParams.get('mode');
    let _mode = StepsFlowEnum.STANDALONE;

    if (modeParams === StepsFlowEnum.STORE_LOCATOR) {
      _mode = StepsFlowEnum.STORE_LOCATOR;
    }

    if (modeParams === StepsFlowEnum.SERVICE) {
      _mode = StepsFlowEnum.SERVICE;
    }

    if (modeParams === StepsFlowEnum.SUPER_MODEL) {
      _mode = StepsFlowEnum.SUPER_MODEL;
    }

    if (modeParams === StepsFlowEnum.BOOK_FOR_OTHER) {
      _mode = StepsFlowEnum.BOOK_FOR_OTHER;
    }

    if (modeParams === StepsFlowEnum.BOOK_FOR_OTHER_WITH_STORE) {
      _mode = StepsFlowEnum.BOOK_FOR_OTHER_WITH_STORE;
    }

    return _mode;
  }, [searchParams]);

  useEffect(() => {
    datadogRum?.setGlobalContextProperty(DatadogRumEnum.MODE, mode);
  }, [mode]);

  const config: StepConfigInterface = useMemo(() => {
    let _config = defaultFlow;

    if (mode === StepsFlowEnum.STORE_LOCATOR) {
      _config = storeLocatorFlow;
    }

    if (mode === StepsFlowEnum.SERVICE) {
      _config = serviceFlow;
    }

    if (mode === StepsFlowEnum.SUPER_MODEL) {
      _config = superModelFlow;
    }

    if (mode === StepsFlowEnum.BOOK_FOR_OTHER) {
      _config = bookForOtherFlow;
    }

    if (mode === StepsFlowEnum.BOOK_FOR_OTHER_WITH_STORE) {
      _config = bookForOtherWithStoreFlow;
    }

    return _config;
  }, [mode]);

  const currentStepIndex = useMemo(() => {
    return (
      config?.steps?.findIndex(({ path }) => pathname?.indexOf(path) >= 0) ?? 0
    );
  }, [pathname, config]);

  const currentStep = useMemo(() => {
    return config?.steps?.find(({ path }) => pathname?.indexOf(path) >= 0);
  }, [pathname, config]);

  const previousStep: StepInterface | undefined = useMemo(() => {
    if (currentStepIndex === 0) {
      return undefined;
    }

    const previousSteps = config?.steps?.slice(0, currentStepIndex) || [];

    return previousSteps.findLast(
      (step) => !step?.hasToSkip?.({ countryService, shop }),
    );
  }, [currentStepIndex, config, countryService, shop]);

  const nextStep: StepInterface | undefined = useMemo(() => {
    if (currentStepIndex >= config?.steps?.length) {
      return undefined;
    }

    const remainingSteps = config?.steps?.slice(currentStepIndex + 1) || [];

    return remainingSteps.find(
      (step) => !step?.hasToSkip?.({ countryService, shop }),
    );
  }, [currentStepIndex, config, countryService, shop]);

  const nextUrl = useMemo(() => {
    if (!nextStep) {
      return `/success?${searchParams?.toString()}`;
    }

    return builtUrl(nextStep?.path);
  }, [searchParams, nextStep, builtUrl]);

  const previousUrl = useMemo(() => {
    return builtUrl(previousStep?.path);
  }, [previousStep, builtUrl]);

  const stepUrl = (step: StepIdEnum) => {
    const targetStep = config?.steps?.find(({ id }) => id === step);
    return targetStep ? builtUrl(targetStep?.path) : '';
  };

  const goToNextStep = async (
    _searchParams: URLSearchParams = new URLSearchParams(),
  ) => {
    const _nextUrl = parsedUrl(nextUrl, _searchParams);

    if (!_nextUrl) {
      return;
    }

    // A valid FED session (e.g. created from a host app token (/api/auth/fed-token)) must be kept as is: no sign out nor FED re-login
    const hasValidFedSession =
      config?.providerId === AuthProviderEnum.FED &&
      session?.token?.provider === AuthProviderEnum.FED &&
      !session?.error &&
      session?.token?.expiresAt * 1000 > Date.now();

    if (hasValidFedSession) {
      router.push(_nextUrl);
      return;
    }

    if (nextStep?.protected) {
      const callbackUrl = _nextUrl;

      await signIn(config?.providerId, {
        callbackUrl,
      });
      return;
    }

    if (
      nextStep?.checkSession &&
      session !== null &&
      (!session?.error as unknown)
    ) {
      const callbackUrl = `/country/${params.countryCode}/steps${RoutesEnum.CONFIRM}?${mergeURLSearchParams(searchParams, _searchParams)?.toString()}`;

      await signIn(config?.providerId, {
        callbackUrl,
      });
      return;
    }

    router.push(_nextUrl);
  };

  const goToStep = (
    step: StepIdEnum,
    _searchParams: URLSearchParams = new URLSearchParams(),
  ) => {
    const _parsedUrl = parsedUrl(stepUrl(step), _searchParams);
    if (!_parsedUrl) {
      return;
    }
    router.push(_parsedUrl);
  };

  return {
    mode,
    config,
    nextUrl,
    currentStep,
    previousUrl,
    goToNextStep,
    goToStep,
    stepUrl,
    builtUrl,
  };
};

export default useConfig;
